import type { Nft } from '@0xtorch/core'
import type { Chain } from '../../chain'
import type { Erc20Token, LowerHex } from '../../types'
import type { EvmAddress } from '../../types/address'
import type { AnalyzeDataSource } from '../../types/analyzeDataSource'
import type { TransactionDetail } from '../../types/transactionDetail'
import { createErc721NftId, createErc1155NftId } from '../../utils'
import type { JsonAnalyzer } from './createActions/json/type'

type GetDataFromDatasourceParameters = {
  readonly chain: Chain
  readonly transaction: TransactionDetail
  readonly datasource: AnalyzeDataSource
  readonly relatedAddresses: Set<LowerHex>
}

type GetDataFromDatasourceReturnTypes = {
  readonly addresses: readonly EvmAddress[]
  readonly erc20Tokens: readonly Erc20Token[]
  readonly eventAbis: readonly {
    signature: LowerHex
    indexedCount: number
    abi: string
  }[]
  readonly functionAbis: readonly string[]
  readonly jsonAnalyzers: readonly JsonAnalyzer[]
  readonly nfts: readonly Nft[]
}

const l2NativeTokenAddress = '0x000000000000000000000000000000000000800a'

export const getDataFromDatasource = async ({
  chain,
  transaction,
  datasource,
  relatedAddresses,
}: GetDataFromDatasourceParameters): Promise<GetDataFromDatasourceReturnTypes> => {
  // Fetch data from datasource
  const erc20TransferAddresses = new Set(
    transaction.erc20Transfers.map((transfer) => transfer.address),
  )
  // l2NativeTokenAddress を erc20TransferAddresses から削除
  erc20TransferAddresses.delete(l2NativeTokenAddress)
  const nftIds = new Set([
    ...transaction.erc721Transfers
      .filter(
        ({ from, to }) =>
          relatedAddresses.has(from) || relatedAddresses.has(to),
      )
      .map(({ address, tokenId }) =>
        createErc721NftId({ chainId: transaction.chainId, address, tokenId }),
      ),
    ...transaction.erc1155Transfers
      .filter(
        ({ from, to }) =>
          relatedAddresses.has(from) || relatedAddresses.has(to),
      )
      .map(({ address, tokenId }) =>
        createErc1155NftId({ chainId: transaction.chainId, address, tokenId }),
      ),
  ])
  const rawAddresses = new Set([
    transaction.from,
    ...(transaction.to === undefined ? [] : [transaction.to]),
  ])
  const eventSignatures = new Set(
    transaction.logs
      .map((log) =>
        (log.eventName !== undefined && log.args !== undefined) ||
        log.topics.length === 0
          ? undefined
          : log.topics[0],
      )
      .filter((signature) => signature !== undefined),
  )
  const functionId =
    transaction.input.length >= 10
      ? (transaction.input.slice(0, 10) as LowerHex)
      : undefined
  const [erc20Tokens, nfts, addresses, functionAbis, eventAbis, jsonAnalyzers] =
    await Promise.all([
      erc20TransferAddresses.size > 0
        ? datasource.getErc20Tokens({
            chainId: transaction.chainId,
            addresses: [...erc20TransferAddresses],
          })
        : ([] as Erc20Token[]),
      nftIds.size > 0
        ? datasource.getNfts({ ids: [...nftIds] })
        : ([] as Nft[]),
      datasource.getAddresses({
        chainId: transaction.chainId,
        addresses: [...rawAddresses],
      }),
      functionId !== undefined
        ? datasource.getFunctionAbi({
            functionId,
          })
        : ([] as Awaited<ReturnType<AnalyzeDataSource['getFunctionAbi']>>),
      eventSignatures.size > 0
        ? datasource.getEventAbis({ signatures: [...eventSignatures] })
        : ([] as Awaited<ReturnType<AnalyzeDataSource['getEventAbis']>>),
      functionId !== undefined
        ? datasource.getJsonAnalyzer({ functionId })
        : ([] as Awaited<ReturnType<AnalyzeDataSource['getJsonAnalyzer']>>),
    ])

  // l2NativeTokenAddress を erc20Tokens に追加
  erc20Tokens.push({
    address: l2NativeTokenAddress,
    name: chain.nativeToken.name,
    symbol: chain.nativeToken.symbol,
    decimals: chain.nativeToken.decimals,
    currency: chain.nativeToken.currency,
  })

  return {
    addresses,
    erc20Tokens,
    eventAbis,
    functionAbis,
    jsonAnalyzers,
    nfts,
  }
}
